import React, { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { fetchApplications, updateApplicationData } from "../../../store/reducers/applicationSlice";
import { createNotification } from "../../../store/reducers/notificationSlice";

import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import iconSearch from "../../../assets/media/icon-search.svg";
import DatePicker from "react-datepicker";
import { AiOutlineEye } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import { Link } from "react-router-dom";
import { showSuccess, showError } from '../../../helpers/toast';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

const AppDiscrepanciesGb = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const authToken = user.access_token;

  const applications = useSelector((state) => state.applications);
  const getallUserdata = useSelector((state) => state.userdata.getalluserdata);
  const [isDownloading, setIsDownloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchBy, setSearchBy] = useState("applicationId");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filteredApplication, setFilteredApplication] = useState([]);
  const [downloadingIds, setDownloadingIds] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handleSearchByChange = (e) => {
    setSearchBy(e.target.value);
  };

  // debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);
  const [modalNote, setModalNote] = useState("");
  const [showModal, setShowModal] = useState(false);

  // Fetch applications when date filter changes - fetch both statuses
  useEffect(() => {
    const fetchBothStatuses = async () => {
      let baseParams = {};

      if (startDate) {
        baseParams.createdFrom = moment(startDate).format("YYYY-MM-DD");
      }
      if (endDate) {
        baseParams.createdTo = moment(endDate).format("YYYY-MM-DD");
      }

      // Add search parameters if debouncedSearchTerm is present
      if (debouncedSearchTerm) {
        baseParams[searchBy] = debouncedSearchTerm;
      }

      const finalParams = {
        ...baseParams,
        state: "descrepency_GB_GB,descrepency_GB_Company,descrepency_GB_Employee"
      };

      // SECURITY: Add company_id filter at API level for company_admin
      if (user.user?.user_role === "company_admin" && user.user?.company_id) {
        finalParams.company_id = user.user.company_id;
      }
      // SECURITY: Add user_id filter at API level for employees
      else if (user.user?.user_role === "employee" && user.user?.id && !debouncedSearchTerm) {
        finalParams.user_id = user.user.id;
      }

      try {
        // Use comma-separated states approach
        const result = await dispatch(
          fetchApplications({
            accessToken: authToken,
            queryParams: finalParams,
            fetchAllPages: true // Fetch all pages to show all applications
          })
        );

      } catch (error) {

      }
    };

    if (authToken) {
      fetchBothStatuses();
    } else {
      console.warn("⚠️ No auth token available, skipping fetch");
    }
  }, [authToken, dispatch, startDate, endDate, user.user?.user_role, user.user?.company_id, user.user?.id, debouncedSearchTerm, searchBy]);

  // Merge and Filter Data
  useEffect(() => {
    if (applications.data?.length > 0) {
      // Debug: Check what states are in the data
      const statesInData = [...new Set(applications.data.map((app) => app.state))];
      console.log("States in applications data:", statesInData);

      const mergedData = applications.data.map((appItem) => {
        const userIdFromApp = appItem.user_id || appItem.userId || appItem.user?.id;
        const userItem = getallUserdata?.find(
          (userData) => userData.id === userIdFromApp
        );
        const finalUserId = userItem?.id || userIdFromApp;
        // Preserve original company_id from application, don't let userItem overwrite it
        const finalCompanyId = appItem.company_id || appItem.companyId || userItem?.company_id || user.user?.company_id;

        if (userItem) {
          return {
            ...appItem,
            applicationId: appItem.id,
            ...userItem,
            user_id: finalUserId, // Preserve original user_id from application
            userId: userItem.id,
            company_id: finalCompanyId, // Preserve original company_id from application
          };
        }
        return {
          ...appItem,
          applicationId: appItem.id,
          user_id: finalUserId, // Preserve original user_id from application
          company_id: finalCompanyId, // Preserve original company_id from application
        };
      });

      console.log("Merged data sample:", mergedData.slice(0, 2));
      console.log("User data:", {
        userId: user.user?.id,
        companyId: user.user?.company_id,
        userDataLength: getallUserdata?.length,
      });

      // SECURITY: Filter by role, company, and state
      const companyAndStateFiltered = mergedData.filter((item) => {
        // Check for both states that we're fetching from API
        const hasCorrectState =
          item.state === "descrepency_GB_GB" ||
          item.state === "descrepency_GB_Company" ||
          item.state === "descrepency_GB_Employee";
        if (!hasCorrectState) return false; // Early exit if state doesn't match

        const userRole = user.user?.user_role;
        const currentUserId = user.user?.id;
        const userCompanyId = user.user?.company_id;
        const itemUserId = item.user_id || item.userId || item.user?.id;
        const itemCompanyId = item.company_id || item.companyId;

        // SECURITY: If company_id is missing, don't show the application
        if (!itemCompanyId || !userCompanyId) {
          console.warn('Missing company_id - filtering out item:', {
            itemId: item.id,
            itemCompanyId,
            userCompanyId
          });
          return false;
        }

        let shouldShow = false;

        if (userRole === "company_admin") {
          // Company admin can see all applications in their company
          const hasCorrectCompany = String(itemCompanyId) === String(userCompanyId);
          shouldShow = hasCorrectCompany;
        } else if (userRole === "employee") {
          // Employee can only see their own applications within their company
          const isOwnApplication = String(itemUserId) === String(currentUserId);
          const hasCorrectCompany = String(itemCompanyId) === String(userCompanyId);
          shouldShow = isOwnApplication && hasCorrectCompany;
        } else {
          // Default: filter by company (fallback)
          const hasCorrectCompany = String(itemCompanyId) === String(userCompanyId);
          shouldShow = hasCorrectCompany;
        }

        console.log('Filtering GB discrepancy item:', {
          id: item.id,
          itemCompanyId,
          userCompanyId,
          itemUserId,
          currentUserId,
          state: item.state,
          userRole,
          shouldShow
        });

        return shouldShow;
      });

      // Then apply search filter
      const filteredData = companyAndStateFiltered.filter((item) => {


        if (!searchQuery && !searchTerm) return true; // Show all if no search query

        const term = searchQuery || searchTerm;
        return Object.values(item).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(term.toLowerCase())
        );
      });

      console.log("GB - Filtered data:", {
        originalData: applications.data?.length,
        mergedData: mergedData.length,
        companyAndStateFiltered: companyAndStateFiltered.length,
        filteredData: filteredData.length,
        searchQuery,
        companyId: user.user.company_id,
        statesInData,
      });

      setFilteredApplication(filteredData);
    } else {
      setFilteredApplication([]); // Clear data if no applications found
    }
    setCurrentPage(1); // Reset to first page when data changes
  }, [searchQuery, applications.data, getallUserdata, user.user.company_id]);

  // Pagination data
  const paginatedData = useMemo(() => {
    return filteredApplication
      .sort((a, b) => b.applicationId - a.applicationId)
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [filteredApplication, currentPage, itemsPerPage]);

  const getTotalPages = () => Math.ceil(filteredApplication.length / itemsPerPage);

  // Download
  const handleDownload = useCallback(
    async (item) => {
      const { applicationId, userId } = item;

      // Prevent multiple clicks
      if (isDownloading || downloadingIds.includes(applicationId)) {
        return;
      }

      setDownloadingIds((prev) => {
        const newIds = [...prev, applicationId];
        return newIds;
      });
      setIsDownloading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}file/${userId}/downloadapplicationfiles/${applicationId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/zip",
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Download failed: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `application_${applicationId}_files.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        showSuccess('Files downloaded successfully!');
      } catch (error) {
        showError(`Download failed: ${error.message}`);
      } finally {
        setDownloadingIds((prev) => {
          const newIds = prev.filter((id) => id !== applicationId);
          return newIds;
        });
        setIsDownloading(false);
      }
    },
    [authToken, downloadingIds, isDownloading]
  );




  const handleAssignToGB = useCallback(async (item) => {
    console.log('🔄 handleAssignToGB called with item:', item);
    try {
      await dispatch(updateApplicationData({
        id: item.applicationId,
        userId: user.user.id,
        state: "descrepency_GB_GB",
        token: authToken,
        json_data: item.json_data,
        files: []
      }));
      dispatch(createNotification({
        user_id: item.user_id || item.userId,
        applicationId: item.applicationId,
        applicationName: item.name,
        status: "descrepency_GB_GB",
        accessToken: authToken,
        sendToGamingBoardAdmins: true,
      }));
      showSuccess('Application assigned to GB successfully!');
      // Refresh the applications list
      dispatch(fetchApplications({
        accessToken: authToken,
        fetchAllPages: true // Fetch all pages to show all applications
      }));
    } catch (error) {
      showError('Failed to assign application to GB');
    }
  }, [dispatch, authToken, user.user.id]);

  const handleAssignToCompany = useCallback(async (item) => {
    try {
      await dispatch(updateApplicationData({
        id: item.applicationId,
        userId: user.user.id,
        state: "descrepency_GB_Company",
        token: authToken,
        json_data: item.json_data,
        files: []
      }));

      // Notify Company Admin & Application Owner
      dispatch(createNotification({
        user_id: item.user_id || item.userId,
        applicationId: item.applicationId,
        applicationName: item.name,
        status: "descrepency_GB_Company",
        accessToken: authToken,
        sendToCompanyAdmins: true,
      }));

      showSuccess('Application assigned to Company successfully!');
      // Refresh the applications list
      dispatch(fetchApplications({
        accessToken: authToken,
        fetchAllPages: true // Fetch all pages to show all applications
      }));
    } catch (error) {
      showError('Failed to assign application to Company');
    }
  }, [dispatch, authToken, user.user.id]);

  const handleAssignToEmployee = useCallback(async (item) => {
    console.log('🔄 handleAssignToEmployee called with item:', item);
    try {
      await dispatch(updateApplicationData({
        id: item.applicationId,
        userId: user.user.id,
        state: "descrepency_GB_Employee",
        token: authToken,
        json_data: item.json_data,
        files: []
      }));
      dispatch(createNotification({
        user_id: item.user_id || item.userId,
        applicationId: item.applicationId,
        applicationName: item.name,
        status: "descrepency_GB_Employee",
        accessToken: authToken,
        sendToCompanyAdmins: true,
      }));
      showSuccess('Application assigned to Employee successfully!');
      // Refresh the applications list
      dispatch(fetchApplications({
        accessToken: authToken,
        fetchAllPages: true // Fetch all pages to show all applications
      }));
    } catch (error) {
      showError('Failed to assign application to Employee');
    }
  }, [dispatch, authToken, user.user.id]);

  // React Table columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * itemsPerPage + info.row.index + 1,
      }),
      columnHelper.accessor("applicationId", {
        header: "Application No.",
        cell: (info) => `GB-${info.getValue()}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong title={row.name}>
                {row.name?.length > 50 ? `${row.name.substring(0, 50)}...` : row.name}
              </strong>
              <span>{row.categoryName}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted By",
        cell: (info) => {
          const row = info.row.original;

          return (
            <div className="boardTitle">
              <strong>{row.companyName || row.company_name || 'N/A'}</strong>
              <span>{row.companyEmail || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedFor",
        header: "Submitted For",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.userName || row.user_name || row.name || 'N/A'}</strong>
              <span>{row.userEmail || row.user_email || row.email || 'N/A'}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("created_at", {
        header: "Created At",
        cell: (info) => (
          <span className="dateText">{info.getValue()?.slice(0, 10)}</span>
        ),
      }),
      columnHelper.accessor("state", {
        header: "Status",
        cell: (info) => (
          <span className="dateText">
            {info.getValue() === "descrepency_GB_GB" ? "Assigned to GB" : info.getValue() === "descrepency_GB_Company" ? "Assigned to Company" : info.getValue() === "descrepency_GB_Employee" ? "Assigned to Employee" : "Unknown"}
          </span>
        ),
      }),
      // columnHelper.accessor("note", {
      //   header: "Note",
      //   cell: (info) => {
      //     const row = info.row.original;
      //     const note =
      //       row.discrepancy &&
      //         row.discrepancy.dis_form2_1 &&
      //         row.discrepancy.dis_form2_1.value
      //         ? row.discrepancy.dis_form2_1.value
      //         : "";

      //     if (!note) {
      //       return <span className="dateText" style={{ color: "#888" }}>No note</span>;
      //     }

      //     return (
      //       <span
      //         className="dateText"
      //         style={{ color: "#007bff", cursor: "pointer", textDecoration: "none" }}
      //         onClick={() => {
      //           setModalNote(note);
      //           setShowModal(true);
      //         }}
      //       >
      //         {note.length > 30 ? note.slice(0, 30) + "..." : note}
      //       </span>
      //     );
      //   },
      // }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    <Link
                      onClick={() => {
                        if (!isDownloading && !downloadingIds.includes(row.applicationId)) {
                          handleDownload(row);
                        }
                      }}
                      className="dropdown-item"
                      style={{
                        opacity: isDownloading || downloadingIds.includes(row.applicationId) ? 0.6 : 1,
                        pointerEvents: isDownloading || downloadingIds.includes(row.applicationId) ? 'none' : 'auto',
                        cursor: isDownloading || downloadingIds.includes(row.applicationId) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isDownloading || downloadingIds.includes(row.applicationId) ? 'Downloading...' : 'Download'}
                    </Link>
                  </li>
                  {((user.user.user_role === "company_admin" && row.state === "descrepency_GB_Company") || (user.user.user_role === "employee" && row.state === "descrepency_GB_Employee")) && (
                    <li>
                      <Link
                        to={`/view-discrepancies/gb/${row.applicationId}`}
                        className="dropdown-item"
                      >
                        View and Edit
                      </Link>
                    </li>
                  )}

                  {user.user.user_role === "company_admin" && row.state === "descrepency_GB_Company" && (
                    <li>
                      <Link
                        onClick={() => handleAssignToEmployee(row)}
                        className="dropdown-item"
                      >
                        Assign to Employee
                      </Link>
                    </li>
                  )}

                  {user.user.user_role === "employee" && row.state === "descrepency_GB_Employee" && (
                    <li>
                      <Link
                        onClick={() => handleAssignToCompany(row)}
                        className="dropdown-item"
                      >
                        Assign to Company
                      </Link>
                    </li>
                  )}
                  {user.user.user_role === "company_admin" && row.state === "descrepency_GB_Company" && (
                    <li>
                      <Link
                        onClick={() => handleAssignToGB(row)}
                        className="dropdown-item"
                      >
                        Assign to GB
                      </Link>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [navigate, handleDownload, handleAssignToEmployee, handleAssignToCompany, handleAssignToGB, user.user.user_role, getallUserdata, currentPage, itemsPerPage]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });


  if (applications.fetchLoading) {
    return (
      <div className="mainApp">
        <Navbar />
        <Header />
        <div className="wrapper">
          <div className="boxAppBoard boxWhite">
            <div className="text-center p-4">
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mainApp">
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          {/* Header with search + date filter */}
          <div className="boardHeader">
            <div className="boardHeadLeft">
              <h2>Discrepancies GB</h2>
            </div>
            <div className="boardHeadRight">
              <select onChange={handleSearchByChange} value={searchBy} >
                <option value="applicationId">Search by Application ID</option>
                <option value="username">Search by Employee Name</option>

              </select>
              <form className="searchForm">
                <input
                  type="text"
                  placeholder="Search"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  value={searchTerm}
                  className="searchInput"
                />
                <button type="button" className="searchBtn">
                  <img src={iconSearch} alt="search" />
                </button>
              </form>
              <div className="datePicker">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    const [start, end] = update;
                    setStartDate(start);
                    setEndDate(end);
                    setCurrentPage(1);
                  }}
                  isClearable={true}
                  placeholderText="Select Date Range"
                  className="date-range-input"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="boardContent table-responsive">
            <table className="table">
              <thead className="">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr className="" key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr className="" key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={table.getHeaderGroups()?.[0]?.headers.length}
                      style={{
                        textAlign: "center",
                        padding: "20px",
                      }}
                    >
                      {applications.fetchLoading ? (
                        <p>Loading applications...</p>
                      ) : applications.fetchError ? (
                        <div>
                          <p style={{ color: 'red' }}>Error loading applications: {applications.fetchError}</p>
                        </div>
                      ) : (
                        <div>
                          <p>No applications found</p>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={getTotalPages}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
          />
        </div>
      </div>
    </div >
  );
};

export default AppDiscrepanciesGb;
